import React from 'react';
import { Box, Typography, TextField, Stack, InputAdornment } from '@mui/material';

const DiscountDetailPanel = ({ discount, orderSubtotal, customValue, onCustomValueChange, reason, onReasonChange }) => {
  if (!discount) return null;

  const sub         = parseFloat(orderSubtotal || 0);
  const baseValue   = parseFloat(discount.discount_value || 0);
  const isPct       = discount.discount_type === 'percentage';
  const effective   = customValue !== '' ? parseFloat(customValue) || 0 : baseValue;
  const savings     = isPct ? (sub * effective / 100).toFixed(2) : Math.min(effective, sub).toFixed(2);
  const minBill     = parseFloat(discount.minimum_bill_value || 0);
  const belowMin    = minBill > 0 && sub < minBill;
  const exceedsBase = customValue !== '' && parseFloat(customValue) > baseValue;

  return (
    <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>

      {/* Savings preview */}
      <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', px: 2, py: 1.25, mb: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 600 }}>
            {isPct ? `${effective}% on ₹${sub.toFixed(2)}` : 'Flat off'}
          </Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#16A34A' }}>
            −₹{savings}
          </Typography>
        </Stack>
        {belowMin && (
          <Typography sx={{ fontSize: '0.7rem', color: '#EF4444', mt: 0.5, fontWeight: 600 }}>
            ⚠ Min bill ₹{discount.minimum_bill_value} required (current ₹{sub.toFixed(2)})
          </Typography>
        )}
        {discount.product_bucket_ids?.length > 0 && (
          <Typography sx={{ fontSize: '0.7rem', color: '#D97706', mt: 0.5, fontWeight: 600 }}>
            ⚠ Restricted to specific products — won't apply if this item isn't in the bucket
          </Typography>
        )}
      </Box>

      {/* Custom value — only when allow_value_change */}
      {discount.allow_value_change && (
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748B', mb: 0.5 }}>
            Modify Value
          </Typography>
          <TextField
            fullWidth size="small" type="number"
            value={customValue}
            onChange={(e) => onCustomValueChange(e.target.value)}
            placeholder={String(baseValue)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', fontWeight: 600 }}>
                    {isPct ? '%' : '₹'}
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F8FAFC', fontSize: '0.9rem' } }}
          />
          {exceedsBase && (
            <Typography sx={{ fontSize: '0.7rem', color: '#F59E0B', mt: 0.5, fontWeight: 600 }}>
              ⚠ Above base value — may require manager approval
            </Typography>
          )}
        </Box>
      )}

      {/* Reason */}
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748B', mb: 0.5 }}>
          Reason <span style={{ color: '#94A3B8', fontWeight: 400 }}>(optional)</span>
        </Typography>
        <TextField
          fullWidth size="small" multiline maxRows={2}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder="e.g. Customer loyalty"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F8FAFC', fontSize: '0.82rem' } }}
        />
      </Box>
    </Box>
  );
};

export default DiscountDetailPanel;
